// SPDX-License-Identifier: AGPL-3.0-or-later
// Voice provider preferences, availability and voice selection.

import {Endpoints} from '@app/features/app/constants/Endpoints';
import styles from '@app/features/konvo_assistant/components/AssistantSpeechControls.module.css';
import {AssistantSpeechPreferences} from '@app/features/konvo_assistant/components/AssistantSpeechPreferences';
import AssistantSpeech, {type VoiceEngineAvailability} from '@app/features/konvo_assistant/state/AssistantSpeech';
import KonvoConnections from '@app/features/konvo_connections/state/KonvoConnections';
import {http} from '@app/features/platform/transport/RestTransport';
import {SegmentedTabs} from '@app/features/ui/segmented_tabs/SegmentedTabs';
import {msg} from '@lingui/core/macro';
import {Trans, useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import {useEffect, useId, useState} from 'react';
import {CODEX_VOICE_PREVIEWS} from '../state/CodexVoicePreviews';
import {useVoicePreview} from './useVoicePreview';

interface GeminiVoiceOption {
	id: string;
	style: string;
}

function useVoiceEngines(): VoiceEngineAvailability | null {
	const [engines, setEngines] = useState<VoiceEngineAvailability | null>(null);
	const codexConnected = KonvoConnections.codex?.connected;
	const codexEnabled = KonvoConnections.codex?.enabled;
	const selectedAssistant = KonvoConnections.assistantProvider;
	useEffect(() => {
		let active = true;
		void http
			.get<VoiceEngineAvailability>(Endpoints.KONVO_ASSISTANT_VOICE_ENGINES)
			.then((response) => {
				if (active && response.body) {
					setEngines(response.body);
					AssistantSpeech.rememberVoiceEngines(response.body);
				}
			})
			.catch(() => undefined);
		return () => {
			active = false;
		};
	}, [codexConnected, codexEnabled, selectedAssistant]);
	return engines;
}

const GeminiVoicePreferences = observer(({voices}: {voices: Array<GeminiVoiceOption>}) => {
	const id = useId();
	const selected = AssistantSpeech.geminiVoice || voices[0]?.id || '';
	const {
		status: previewing,
		failed,
		preview,
		stop,
	} = useVoicePreview(async () => {
		const response = await http.post<Blob>(Endpoints.KONVO_ASSISTANT_VOICE_GEMINI_PREVIEW, {
			body: {voice: selected},
			parse: 'binary',
		});
		if (!(response.body instanceof Blob)) throw new Error('No audio');
		const url = URL.createObjectURL(response.body);
		return {url, dispose: () => URL.revokeObjectURL(url)};
	});
	return (
		<div className={styles.preferences} data-flx="konvo-assistant.gemini-voice-preferences">
			<label htmlFor={`${id}-voice`}>
				<Trans>Voice</Trans>
			</label>
			<select
				id={`${id}-voice`}
				className={styles.select}
				value={selected}
				onChange={(event) => {
					stop();
					AssistantSpeech.setGeminiVoice(event.target.value);
				}}
			>
				{voices.map((voice) => (
					<option key={voice.id} value={voice.id}>
						{`${voice.id} · ${voice.style}`}
					</option>
				))}
			</select>
			<p className={styles.hint}>
				<Trans>
					Google's Gemini hears you and speaks, and hands anything about your mail, calendar, chats and tasks to the
					Assistant. Your voice and the Assistant's answers go to Google, which may use them to improve its products.
					Applies the next time you start voice mode. Saved on this device.
				</Trans>
			</p>
			<button type="button" className={styles.button} disabled={!selected} onClick={() => void preview()}>
				{previewing === 'loading' ? (
					<Trans>Loading preview…</Trans>
				) : previewing === 'playing' ? (
					<Trans>Stop preview</Trans>
				) : (
					<Trans>Preview voice</Trans>
				)}
			</button>
			{failed && (
				<p role="alert" className={styles.error}>
					<Trans>The preview could not be loaded. Try again in a moment.</Trans>
				</p>
			)}
		</div>
	);
});

const CodexVoicePreferences = observer(({engines}: {engines: VoiceEngineAvailability}) => {
	const id = useId();
	const selected = AssistantSpeech.codexVoice || engines.codex_default_voice || 'cove';
	const {
		status: previewing,
		failed,
		preview,
		stop,
	} = useVoicePreview(async () => {
		const url = CODEX_VOICE_PREVIEWS[selected];
		if (!url) throw new Error('No preview for this voice');
		return {url};
	});
	const defaultVoice = engines.codex_default_voice
		? engines.codex_default_voice.charAt(0).toUpperCase() + engines.codex_default_voice.slice(1)
		: 'Cove';
	return (
		<div className={styles.preferences} data-flx="konvo-assistant.codex-voice-preferences">
			<label htmlFor={`${id}-voice`}>
				<Trans>Voice</Trans>
			</label>
			<select
				id={`${id}-voice`}
				className={styles.select}
				value={AssistantSpeech.codexVoice}
				onChange={(event) => {
					stop();
					AssistantSpeech.setCodexVoice(event.target.value);
				}}
			>
				<option value="">
					<Trans>Codex default ({defaultVoice})</Trans>
				</option>
				{(engines.codex_voices ?? []).map((voice) => (
					<option key={voice} value={voice}>
						{voice.charAt(0).toUpperCase() + voice.slice(1)}
					</option>
				))}
			</select>
			<button
				type="button"
				className={styles.button}
				disabled={!CODEX_VOICE_PREVIEWS[selected]}
				onClick={() => void preview()}
			>
				{previewing === 'loading' ? (
					<Trans>Loading preview…</Trans>
				) : previewing === 'playing' ? (
					<Trans>Stop preview</Trans>
				) : (
					<Trans>Preview voice</Trans>
				)}
			</button>
			{failed && (
				<p role="alert" className={styles.error}>
					<Trans>The preview could not be loaded. Try again in a moment.</Trans>
				</p>
			)}
			<label htmlFor={`${id}-assistant`}>
				<Trans>Workplace assistant</Trans>
			</label>
			<select
				id={`${id}-assistant`}
				className={styles.select}
				value={AssistantSpeech.voiceEngine === 'codex-voice' ? 'codex-voice' : 'codex-live'}
				onChange={(event) => AssistantSpeech.setVoiceEngine(event.target.value as 'codex-live' | 'codex-voice')}
			>
				<option value="codex-live">
					<Trans>Codex Live</Trans>
				</option>
				<option value="codex-voice">
					<Trans>My selected assistant</Trans>
				</option>
			</select>
			<p className={styles.hint}>
				{AssistantSpeech.voiceEngine === 'codex-voice' ? (
					<Trans>Codex hears you and speaks. Your selected assistant handles workplace tasks.</Trans>
				) : (
					<Trans>Codex hears you, handles workplace tasks and speaks.</Trans>
				)}{' '}
				<Trans>Applies the next time you start voice mode. Saved on this device.</Trans>
			</p>
		</div>
	);
});

export const AssistantVoiceSettings = observer(() => {
	const {i18n} = useLingui();
	const engines = useVoiceEngines();
	const engine =
		engines && AssistantSpeech.voiceEnginePreference === 'automatic'
			? engines.codex
				? 'codex-live'
				: engines.gemini
					? 'gemini'
					: 'konvo'
			: AssistantSpeech.voiceEngine;
	const selectedTab = engine.startsWith('codex') ? 'codex' : (engine as 'konvo' | 'gemini');
	return (
		<div className={styles.preferences} data-flx="konvo-assistant.voice-settings">
			<SegmentedTabs<'konvo' | 'gemini' | 'codex'>
				tabs={[
					{id: 'konvo', label: i18n._(msg`Konvo voices`)},
					{id: 'gemini', label: i18n._(msg`Gemini Live`)},
					{id: 'codex', label: i18n._(msg`Codex voices`)},
				]}
				selectedTab={selectedTab}
				onTabChange={(tab) => AssistantSpeech.setVoiceEngine(tab === 'codex' ? AssistantSpeech.codexVoiceMode : tab)}
				ariaLabel={i18n._(msg`Voice settings`)}
			/>
			{selectedTab === 'konvo' ? (
				<AssistantSpeechPreferences />
			) : selectedTab === 'gemini' ? (
				engines?.gemini ? (
					<GeminiVoicePreferences voices={engines.gemini_voices ?? []} />
				) : (
					<p className={styles.hint}>
						<Trans>Gemini Live is currently unavailable. Voice mode will use Konvo voices.</Trans>
					</p>
				)
			) : engines?.codex ? (
				<CodexVoicePreferences engines={engines} />
			) : (
				<p className={styles.hint}>
					<Trans>Connect and enable Codex in Assistant settings to use Codex Live and Codex Voice.</Trans>
				</p>
			)}
		</div>
	);
});
